// StructuralPhotoManager.js
// Structural Photo Manager: site systems checklist, then one card per form
// section with its photos (one or more, captioned with the section heading),
// every line on the form for that section, and its repair items. Confirm per
// card; any edit un-confirms it. Same workflow as the electrical Photo Manager.

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import {
  getStructuralSections, getStructuralSystems, STRUCTURAL_LEAD, effectiveAnswers,
  numberStructuralPhotos, sectionIsNA, NA_TEXT, NONE_OBSERVED, answerNeedsRepair,
} from '../structuralItems';
import {
  loadStructural, saveStructuralSlot, saveStructuralSystems, emptyStructuralSlot, isConfirmed,
  addPhoto, removePhoto, movePhoto, setAnswer, addRepair, updateRepair, removeRepair,
  addRepairPhoto, removeRepairPhoto, detachPhoto, setNA, confirmStructural, structuralProblems,
} from '../structuralSlots';
import { disciplinePaths } from '../disciplines';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import '../App.css';

const photoUrl = (p) => (p ? (p.thumbUrl || p.fullUrl || p.url) : null);
const isStructuralPhoto = (p) => p?.discipline === 'structural';
const PASS = ['Good', 'N/A', 'No', 'None visible', 'None Visible', 'Not Significant', 'Not significant', 'Repairs Not Required', 'None Observed'];
const FAIL = /significant|poor|repairs required|needs repair|needs attention/i;

const StructuralPhotoManager = () => {
  const { projectId }   = useParams();
  const { currentUser } = useAuth();
  const location        = useLocation();
  const navigate        = useNavigate();

  const [project, setProject]         = useState(null);
  const [photos, setPhotos]           = useState([]);
  const [slots, setSlots]             = useState({});
  const [siteSystems, setSiteSystems] = useState({});
  const [screen, setScreen]           = useState('slots');   // 'checklist' | 'slots'
  const [drafts, setDrafts]           = useState({});
  const [loading, setLoading]         = useState(true);
  const [saveError, setSaveError]     = useState('');
  const [uploading, setUploading]     = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [picker, setPicker]           = useState(null);      // { key, mode: 'section'|'repair', repairId, label }
  const [showOther, setShowOther]     = useState(false);     // include electrical photos in the picker
  const [confirmMove, setConfirmMove] = useState(null);      // { photo, fromLabel }
  const [highlight, setHighlight]     = useState('');

  const county   = project?.county;
  const sections = useMemo(() => (project ? getStructuralSections(county) : []), [project, county]);
  const systems  = useMemo(() => (project ? getStructuralSystems(county) : []), [project, county]);
  const allItems = useMemo(() => [...STRUCTURAL_LEAD, ...sections], [sections]);

  // ─── Load ───────────────────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        const proj = projectDoc.exists() ? projectDoc.data() : null;
        setProject(proj);

        const snap = await getDocs(collection(db, 'projects', projectId, 'photos'));
        setPhotos(snap.docs.map(d => ({ id: d.id, ...d.data() })));

        const report = await loadStructural(projectId, proj?.county);
        setSlots(report.slots);
        setSiteSystems(report.siteSystems);

        const unanswered = getStructuralSystems(proj?.county).some(s => typeof report.siteSystems?.[s.key]?.present !== 'boolean');
        setScreen(unanswered ? 'checklist' : 'slots');
      } catch (err) {
        console.error('Error loading structural Photo Manager:', err);
        setSaveError('Could not load this project. Refresh to try again.');
      }
      setLoading(false);
    };
    load();
  }, [projectId]);

  // Jump to a section (#st_07) from Report Details
  useEffect(() => {
    if (loading || screen !== 'slots') return undefined;
    const target = (location.hash || '').replace('#', '');
    const el = target && document.getElementById(target);
    if (!el) return undefined;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setHighlight(target);
    const t = setTimeout(() => setHighlight(''), 2500);
    return () => clearTimeout(t);
  }, [loading, screen, location.hash]);

  // ─── Lookups ────────────────────────────────────────────────────────────

  const photoById = (id) => photos.find(p => p.id === id) || null;
  const slotOf    = (key) => slots[key] || emptyStructuralSlot();

  // Photo numbers as they will print
  const cells = useMemo(() => (project ? numberStructuralPhotos(county, slots, siteSystems) : []), [project, county, slots, siteSystems]);
  const usage = useMemo(() => {
    const map = {};
    cells.forEach(c => { if (c.photoId) map[c.photoId] = c; });
    return map;
  }, [cells]);
  const labelFor = (photoId) => usage[photoId]?.label || 'PHOTO-??';
  const rangeFor = (key) => {
    const own = cells.filter(c => c.key === key && !c.repair);
    if (!own.length) return '';
    return own.length === 1 ? own[0].label : `${own[0].label} – ${own[own.length - 1].label.replace('PHOTO-', '')}`;
  };

  const pool = photos.filter(p => showOther || isStructuralPhoto(p));
  const unusedCount = photos.filter(p => isStructuralPhoto(p) && !usage[p.id]).length;

  // ─── Save ───────────────────────────────────────────────────────────────

  const commit = async (key, next) => {
    setSlots(prev => ({ ...prev, [key]: next }));
    try {
      await saveStructuralSlot(projectId, key, next, currentUser.uid);
      setSaveError('');
    } catch (err) {
      console.error('Structural slot save error:', err);
      setSaveError('Last change did not save. Check your connection and try again.');
    }
  };

  // ─── Site systems ───────────────────────────────────────────────────────

  const answerSystem = (key, present) => setSiteSystems(s => ({ ...s, [key]: { present } }));
  const unansweredCount = systems.filter(s => typeof siteSystems?.[s.key]?.present !== 'boolean').length;

  const applyChecklist = async () => {
    try {
      await saveStructuralSystems(projectId, siteSystems);
      // A changed answer can change what a confirmed section prints — reopen those sections
      for (const sec of sections) {
        const slot = slotOf(sec.key);
        if (isConfirmed(slot) && structuralProblems(sec, slot, siteSystems).length > 0) {
          await commit(sec.key, { ...slot, review: 'edited', confirmedBy: null, confirmedAt: null });
        }
      }
      setScreen('slots');
      setSaveError('');
    } catch (err) {
      console.error('Checklist save error:', err);
      setSaveError('Site systems did not save. Check your connection and try again.');
    }
  };

  // ─── Photo placement ────────────────────────────────────────────────────

  const attach = (slot, target, photoId) => (
    target.mode === 'repair' ? addRepairPhoto(slot, target.repairId, photoId) : addPhoto(slot, photoId)
  );

  const placePhoto = async (photoId, target) => {
    const used = usage[photoId];
    let current = slotOf(target.key);
    if (used) {
      if (used.key === target.key) current = detachPhoto(current, photoId);
      else await commit(used.key, detachPhoto(slotOf(used.key), photoId));
    }
    await commit(target.key, attach(current, target, photoId));
    setPicker(null);
    setConfirmMove(null);
  };

  const retag = async (photo, discipline) => {
    try {
      await updateDoc(doc(db, 'projects', projectId, 'photos', photo.id), { discipline });
      setPhotos(prev => prev.map(p => (p.id === photo.id ? { ...p, discipline } : p)));
    } catch (err) {
      console.error('Re-tag error:', err);
      setSaveError('Could not change the photo’s discipline. Try again.');
    }
  };

  const uploadTo = (file, target) => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    const filename   = `${Date.now()}_${file.name}`;
    const task       = uploadBytesResumable(ref(storage, `projects/${projectId}/photos/${filename}`), file);
    task.on('state_changed',
      snap => setUploadProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      err  => { console.error(err); setUploading(false); setSaveError(`Upload failed (${err.code || err.message}). Try again.`); },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          const docRef = await addDoc(collection(db, 'projects', projectId, 'photos'), {
            url, filename, caption: file.name, source: 'web-upload', discipline: 'structural',
            uploadedBy: currentUser.uid, uploadedAt: serverTimestamp(),
          });
          setPhotos(prev => [...prev, { id: docRef.id, url, filename, caption: file.name, discipline: 'structural' }]);
          await commit(target.key, attach(slotOf(target.key), target, docRef.id));
        } catch (err) {
          console.error('Upload save error:', err);
          setSaveError('Photo uploaded but could not be placed. Use Choose photo to place it.');
        }
        setUploading(false);
        setUploadProgress(0);
      });
  };

  // ─── Text drafts (saved on blur) ────────────────────────────────────────

  const draftVal  = (k, saved) => (drafts[k] !== undefined ? drafts[k] : saved);
  const typeDraft = (k, text) => setDrafts(prev => ({ ...prev, [k]: text }));
  const dropDraft = (k) => setDrafts(prev => { const n = { ...prev }; delete n[k]; return n; });

  const saveAnswerDraft = async (key, id) => {
    const k = `${key}:${id}`;
    const text = drafts[k];
    if (text === undefined) return;
    const current = slotOf(key);
    if ((current.answers?.[id] ?? '') !== text) await commit(key, setAnswer(current, id, text));
    dropDraft(k);
  };

  const saveRepairText = async (key, repairId) => {
    const k = `${key}:rep:${repairId}`;
    const text = drafts[k];
    if (text === undefined) return;
    const current = slotOf(key);
    const rep = current.repairs.find(r => r.id === repairId);
    if (rep && rep.text !== text) await commit(key, updateRepair(current, repairId, { text }));
    dropDraft(k);
  };

  // ─── Progress ───────────────────────────────────────────────────────────

  const ready = (it) => isConfirmed(slots[it.key]) && structuralProblems(it, slots[it.key], siteSystems).length === 0;
  const confirmedCount = allItems.filter(ready).length;
  const aiCount        = allItems.filter(it => slots[it.key]?.review === 'ai').length;
  const openLabels     = allItems.filter(it => !ready(it)).map(it => (it.lead ? it.title : `Sec. ${it.num}`));

  // ─── Checklist ──────────────────────────────────────────────────────────

  const renderChecklist = () => (
    <section aria-label="Site systems" style={styles.card}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        <h3 style={styles.cardTitle}>Site systems</h3>
        <p style={styles.cardText}>
          Answer each before the photo review opens. Anything not present prints as N/A: a whole section prints a NOT APPLICABLE panel and ticks the form’s N/A box; a line prints N/A. The AI skips them.
        </p>
      </div>
      {systems.map(sys => {
        const present = siteSystems?.[sys.key]?.present;
        return (
          <div key={sys.key} style={styles.sysRow}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: '1 1 320px', minWidth: 0 }}>
              <span style={styles.sysTitle}>{sys.title}</span>
              <span style={styles.printsLine}>{sys.note}</span>
            </div>
            <div role="group" aria-label={sys.title} style={styles.btnRow}>
              <button type="button" aria-pressed={present === true} style={present === true ? styles.choiceOnPass : styles.choiceOff}
                onClick={() => answerSystem(sys.key, true)}>Present</button>
              <button type="button" aria-pressed={present === false} style={present === false ? styles.choiceOnNa : styles.choiceOff}
                onClick={() => answerSystem(sys.key, false)}>Not present</button>
            </div>
          </div>
        );
      })}
      <div style={styles.checklistFoot}>
        {unansweredCount > 0 && <span style={styles.warnText}>{unansweredCount} still to answer</span>}
        <button type="button" style={unansweredCount === 0 ? styles.primaryBtnLg : styles.disabledBtnLg}
          disabled={unansweredCount > 0} onClick={applyChecklist}>Open photo review</button>
      </div>
    </section>
  );

  // ─── Picker ─────────────────────────────────────────────────────────────

  const renderPicker = () => {
    const sorted = [...pool].sort((a, b) => (usage[a.id] ? 1 : 0) - (usage[b.id] ? 1 : 0));
    const close = () => { setPicker(null); setConfirmMove(null); };
    return (
      <div style={styles.modalOverlay} onClick={close}>
        <div style={styles.modalBox} onClick={e => e.stopPropagation()} role="dialog" aria-label="Choose photo">
          <div style={styles.modalHeader}>
            <span style={styles.modalTitle}>Choose photo — {picker.label}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <label style={styles.checkRow}>
                <input type="checkbox" checked={showOther} onChange={e => setShowOther(e.target.checked)} />
                <span>Show electrical photos</span>
              </label>
              <button type="button" style={styles.modalClose} aria-label="Close" onClick={close}>✕</button>
            </div>
          </div>

          {confirmMove && (
            <div style={styles.confirmBanner}>
              <span>This photo is already used as <strong>{confirmMove.fromLabel}</strong>. Move it to {picker.label}?</span>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button type="button" style={styles.confirmYes} onClick={() => placePhoto(confirmMove.photo.id, picker)}>Yes, move it</button>
                <button type="button" style={styles.confirmNo} onClick={() => setConfirmMove(null)}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ flex: '1 1 0', minHeight: 0, overflowY: 'auto' }}>
            {sorted.length === 0 ? (
              <div style={styles.modalEmpty}>
                <p>No structural photos in this project yet.</p>
                <p style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>Use Upload on the section, or tick “Show electrical photos” to use one taken for the electrical report.</p>
              </div>
            ) : (
              <div style={styles.pickerGrid}>
                {sorted.map(p => {
                  const used = usage[p.id];
                  const structural = isStructuralPhoto(p);
                  return (
                    <div key={p.id} style={{ ...styles.pickerCard, borderColor: used ? '#b45309' : '#e5e7eb' }}>
                      <button type="button" style={styles.pickerImgBtn}
                        onClick={() => (used ? setConfirmMove({ photo: p, fromLabel: `${used.label} ${used.caption}` }) : placePhoto(p.id, picker))}>
                        <img src={photoUrl(p)} alt={p.caption || p.filename || 'Field photo'} style={styles.pickerThumb}
                          onError={e => { e.target.style.display = 'none'; }} />
                      </button>
                      <span style={styles.pickerCaption}>
                        {used
                          ? <span style={{ color: '#92400e', fontWeight: 600 }}>Used as {used.label} {used.caption}</span>
                          : <span style={{ color: '#065f46', fontWeight: 600 }}>Not used yet</span>}
                        <span style={{ color: structural ? '#1e3a8a' : '#4b5563' }}>
                          {structural ? 'Structural' : 'Electrical'} ·{' '}
                          <button type="button" style={styles.linkBtn} onClick={() => retag(p, structural ? 'electrical' : 'structural')}>
                            Tag as {structural ? 'electrical' : 'structural'}
                          </button>
                        </span>
                        {(p.notes || p.caption) && <span style={{ color: '#4b5563' }}>{p.notes || p.caption}</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── Photo strip (section, lead or repair photos) ───────────────────────

  const renderStrip = ({ ids, key, target, onRemove, onMove, caption, max = 99 }) => (
    <div style={styles.strip}>
      {ids.map((id, i) => {
        const p = photoById(id);
        return (
          <figure key={id} style={styles.thumbFig}>
            {p ? <img src={photoUrl(p)} alt={`${labelFor(id)} ${caption}`} style={styles.thumbnail} />
               : <div style={styles.missingThumb}>Photo not found</div>}
            <figcaption style={styles.thumbCap}><strong>{labelFor(id)}</strong> {caption}</figcaption>
            <div style={styles.btnRow}>
              {onMove && i > 0 && <button type="button" style={styles.iconBtn} aria-label="Move earlier" onClick={() => onMove(i, -1)}>←</button>}
              {onMove && i < ids.length - 1 && <button type="button" style={styles.iconBtn} aria-label="Move later" onClick={() => onMove(i, 1)}>→</button>}
              <button type="button" style={styles.removeBtn} onClick={() => onRemove(id)}>Remove</button>
            </div>
          </figure>
        );
      })}
      {ids.length < max && (
        <div style={styles.addTile}>
          <button type="button" style={styles.primaryBtn} onClick={() => setPicker({ ...target, label: caption })}>Choose photo</button>
          <label style={styles.uploadLabel}>
            Upload
            <input type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => { uploadTo(e.target.files[0], target); e.target.value = ''; }} />
          </label>
        </div>
      )}
    </div>
  );

  // ─── Lines ──────────────────────────────────────────────────────────────

  const choiceStyle = (selected, opt) => {
    if (!selected) return styles.choiceOff;
    if (PASS.includes(opt)) return styles.choiceOnPass;
    if (FAIL.test(opt)) return styles.choiceOnFail;
    return styles.choiceOnNa;
  };

  const renderTextInput = (key, id, value, { short, placeholder, label, disabled } = {}) => {
    const k = `${key}:${id}`;
    const v = draftVal(k, value ?? '');
    const common = {
      style: styles.textInput, value: v, disabled, 'aria-label': label, placeholder: placeholder || '',
      onChange: e => typeDraft(k, e.target.value), onBlur: () => saveAnswerDraft(key, id),
    };
    return short ? <input {...common} /> : <textarea rows={2} {...common} style={{ ...styles.textInput, resize: 'vertical' }} />;
  };

  const renderLine = (sec, slot, line, answers, locked) => {
    if (locked[line.id] === 'not asked') return null;
    const value = answers[line.id];
    const isLocked = !!locked[line.id];
    const set = (v) => commit(sec.key, setAnswer(slot, line.id, v));

    if (isLocked) {
      return (
        <div key={line.id} style={styles.lineBlock}>
          <span style={styles.lineLabel}>{line.label}</span>
          <span style={styles.lockedVal}>{value} <em style={{ fontWeight: 400 }}>(from site systems)</em></span>
        </div>
      );
    }

    let control;
    if (line.kind === 'rating' || line.kind === 'yesno') {
      control = (
        <div role="group" aria-label={line.label} style={styles.btnRow}>
          {line.options.map(opt => (
            <button key={opt} type="button" aria-pressed={value === opt} style={choiceStyle(value === opt, opt)}
              onClick={() => set(value === opt && !line.def ? '' : opt)}>{opt}</button>
          ))}
        </div>
      );
    } else if (line.kind === 'select') {
      control = (
        <select style={{ ...styles.textInput, maxWidth: '360px' }} value={value || ''} aria-label={line.label} onChange={e => set(e.target.value)}>
          <option value="">— Select —</option>
          {line.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    } else if (line.kind === 'radio') {
      const sel = line.options.find(o => o.v === value);
      control = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <div role="radiogroup" aria-label={line.label} style={styles.btnRow}>
            {line.options.map(o => (
              <button key={o.v} type="button" role="radio" aria-checked={value === o.v} style={choiceStyle(value === o.v, o.v)}
                onClick={() => set(o.v)}>{o.v}</button>
            ))}
          </div>
          {sel?.text && renderTextInput(sec.key, `${line.id}_txt`, answers[`${line.id}_txt`], { label: `${line.label} — describe`, placeholder: 'Describe' })}
        </div>
      );
    } else {
      control = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
          {renderTextInput(sec.key, line.id, value, { short: line.short, label: line.label })}
          {!(draftVal(`${sec.key}:${line.id}`, value ?? '') || '').trim() && (
            <div style={styles.btnRow}>
              <button type="button" style={styles.chip} onClick={() => set(NONE_OBSERVED)}>{NONE_OBSERVED}</button>
              <button type="button" style={styles.chip} onClick={() => set(NA_TEXT)}>{NA_TEXT}</button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={line.id} style={styles.lineBlock}>
        <span style={styles.lineLabel}>{line.label}{line.opt ? <em style={styles.optTag}> optional</em> : null}</span>
        {control}
        {line.text && line.kind !== 'radio' && line.kind !== 'text' &&
          renderTextInput(sec.key, `${line.id}_txt`, answers[`${line.id}_txt`], { short: true, label: `${line.label} — comment`, placeholder: 'Comment (optional)' })}
      </div>
    );
  };

  // ─── Repairs ────────────────────────────────────────────────────────────

  const renderRepairs = (sec, slot) => (
    <div style={styles.repairBox}>
      <span style={styles.detailsTitle}>Repair items</span>
      {slot.repairs.length === 0 && <span style={styles.printsLine}>None. Add one for each repair; each needs a description and at least one photo.</span>}
      {slot.repairs.map((rep, i) => {
        const k = `${sec.key}:rep:${rep.id}`;
        const caption = `REPAIR ITEM ${rep.ref || sec.num}`;
        return (
          <div key={rep.id} style={styles.repairRow}>
            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={styles.extraBadge}>Repair {i + 1}</span>
              <label style={styles.inlineLabel}>
                Form item
                <select style={styles.textInput} value={rep.ref} onChange={e => commit(sec.key, updateRepair(slot, rep.id, { ref: e.target.value }))}>
                  {sec.refs.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
              <button type="button" style={{ ...styles.removeBtn, marginLeft: 'auto' }}
                onClick={() => { if (window.confirm('Remove this repair item and unlink its photos?')) commit(sec.key, removeRepair(slot, rep.id)); }}>Remove repair</button>
            </div>
            <textarea rows={2} style={{ ...styles.textInput, resize: 'vertical' }} aria-label={`Repair ${i + 1} description`}
              placeholder="Describe the repair required (prints on the form)"
              value={draftVal(k, rep.text)} onChange={e => typeDraft(k, e.target.value)} onBlur={() => saveRepairText(sec.key, rep.id)} />
            {renderStrip({
              ids: rep.photos, key: sec.key, caption,
              target: { key: sec.key, mode: 'repair', repairId: rep.id },
              onRemove: (id) => commit(sec.key, removeRepairPhoto(slot, rep.id, id)),
            })}
          </div>
        );
      })}
      <button type="button" style={{ ...styles.secondaryBtn, alignSelf: 'flex-start' }}
        onClick={() => commit(sec.key, addRepair(slot, sec.refs[0]))}>+ Add repair item</button>
    </div>
  );

  // ─── Cards ──────────────────────────────────────────────────────────────

  const renderHeader = (item, slot, badge) => (
    <div style={styles.slotHeader}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        {badge && <span style={styles.photoNumBadge}>{badge}</span>}
        <h3 style={styles.slotTitle}>{item.lead ? item.title : `${item.num}. ${item.title}`}</h3>
      </div>
      <div style={styles.btnRow}>
        {isConfirmed(slot) && <span style={{ ...styles.pill, ...styles.pillConfirmed }}>Confirmed</span>}
        {slot.review === 'ai' && <span style={{ ...styles.pill, ...styles.pillAi }}>AI suggestion</span>}
        {slot.review === 'edited' && <span style={{ ...styles.pill, ...styles.pillEdited }}>Edited — not confirmed</span>}
      </div>
    </div>
  );

  const renderFoot = (item, slot) => {
    const problems = structuralProblems(item, slot, siteSystems);
    const confirmed = isConfirmed(slot);
    return (
      <div style={styles.slotFoot}>
        <span />
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {problems.length > 0 && <span style={styles.warnText}>{confirmed ? 'Confirmed, but needs attention: ' : ''}{problems.join(' ')}</span>}
          {!confirmed && (
            <button type="button" style={problems.length === 0 ? styles.confirmBtn : styles.disabledBtn} disabled={problems.length > 0}
              onClick={() => commit(item.key, confirmStructural(slot, currentUser.uid))}>Confirm</button>
          )}
        </div>
      </div>
    );
  };

  const renderLead = (item) => {
    const slot = slotOf(item.key);
    return (
      <section key={item.key} id={item.key} style={{ ...styles.slotCard, ...(highlight === item.key ? styles.slotHighlight : {}) }} aria-label={item.title}>
        {renderHeader(item, slot, rangeFor(item.key))}
        {item.key === 'st_elev' && <span style={styles.printsLine}>Add one photo per building elevation.</span>}
        {renderStrip({
          ids: slot.photos, key: item.key, caption: item.title, max: item.max,
          target: { key: item.key, mode: 'section' },
          onRemove: (id) => commit(item.key, removePhoto(slot, id)),
          onMove: item.max > 1 ? (i, dir) => commit(item.key, movePhoto(slot, i, dir)) : null,
        })}
        {renderFoot(item, slot)}
      </section>
    );
  };

  const renderSection = (sec) => {
    const slot = slotOf(sec.key);
    const naFromList = siteSystems?.[sec.naBy]?.present === false;
    const na = sectionIsNA(sec, slot, siteSystems);
    const { answers, locked } = effectiveAnswers(sec, slot, siteSystems);
    const needsRepair = !na && answerNeedsRepair(sec, answers);

    return (
      <section key={sec.key} id={sec.key} style={{ ...styles.slotCard, ...(highlight === sec.key ? styles.slotHighlight : {}) }} aria-label={`${sec.num}. ${sec.title}`}>
        {renderHeader(sec, slot, rangeFor(sec.key))}

        {naFromList
          ? <span style={{ ...styles.pill, ...styles.pillNa, alignSelf: 'flex-start' }}>N/A from site systems</span>
          : (
            <label style={styles.naRow}>
              <input type="checkbox" checked={na} onChange={e => commit(sec.key, setNA(slot, e.target.checked))} />
              <span>Section not applicable</span>
            </label>
          )}

        {na ? (
          <div style={styles.naPanel}>NOT APPLICABLE</div>
        ) : (
          <>
            {renderStrip({
              ids: slot.photos, key: sec.key, caption: sec.title,
              target: { key: sec.key, mode: 'section' },
              onRemove: (id) => commit(sec.key, removePhoto(slot, id)),
              onMove: (i, dir) => commit(sec.key, movePhoto(slot, i, dir)),
            })}
            <div style={styles.linesBox}>
              <span style={styles.detailsTitle}>Form lines</span>
              {sec.lines.map(line => renderLine(sec, slot, line, answers, locked))}
            </div>
            {needsRepair && slot.repairs.length === 0 && <div style={styles.flag}>An answer says repairs are required. Add a repair item with a photo.</div>}
            {renderRepairs(sec, slot)}
          </>
        )}
        {renderFoot(sec, slot)}
      </section>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  if (loading) return <div style={{ marginLeft: '220px', padding: '2rem' }}>Loading...</div>;

  return (
    <div>
      <Sidebar />
      {picker && renderPicker()}
      <div style={styles.container}>
        <div style={styles.titleRow}>
          <div>
            <h2 style={styles.title}>Structural — Report Editor</h2>
            {project && <p style={styles.subtitle}>{project.address}{project.city ? `, ${project.city}` : ''} — {project.county}</p>}
          </div>
          {screen === 'slots' && (
            <div style={styles.btnRow}>
              <button type="button" style={styles.secondaryBtnLg} onClick={() => setScreen('checklist')}>Site systems</button>
              <button type="button" style={styles.secondaryBtnLg} onClick={() => navigate(disciplinePaths(projectId, 'structural').details)}>Project Details</button>
            </div>
          )}
        </div>

        {saveError && <div role="alert" style={styles.errorBanner}>{saveError}</div>}
        {uploading && (
          <div style={styles.uploadProgressBar}>
            <div style={{ ...styles.uploadProgressFill, width: `${uploadProgress}%` }} />
            <span style={styles.uploadProgressText}>Uploading... {uploadProgress}%</span>
          </div>
        )}

        {screen === 'checklist' ? renderChecklist() : (
          <div style={styles.slotList}>
            <section aria-label="Progress" style={styles.progressCard}>
              <div style={{ display: 'flex', gap: '1.75rem', flexWrap: 'wrap' }}>
                <div style={styles.stat}><span style={styles.statNum}>{confirmedCount} / {allItems.length}</span><span style={styles.statLabel}>Cards confirmed</span></div>
                <div style={styles.stat}><span style={{ ...styles.statNum, color: '#1d4ed8' }}>{aiCount}</span><span style={styles.statLabel}>AI suggestions to review</span></div>
                <div style={styles.stat}><span style={styles.statNum}>{cells.length}</span><span style={styles.statLabel}>Photos in report</span></div>
                <div style={styles.stat}><span style={styles.statNum}>{unusedCount}</span><span style={styles.statLabel}>Structural photos not used</span></div>
              </div>
              {openLabels.length === 0
                ? <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#065f46' }}>All cards confirmed</span>
                : <span style={{ fontSize: '0.8rem', color: '#4b5563', maxWidth: '420px' }}>Report generation unlocks when every card is confirmed. Open: {openLabels.join(', ')}</span>}
            </section>
            {STRUCTURAL_LEAD.map(renderLead)}
            {sections.map(renderSection)}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

// ─── Styles (match the electrical Photo Manager) ──────────────────────────────

const choiceBase = { minHeight: '36px', padding: '0 0.8rem', borderRadius: '8px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit' };

const styles = {
  container:          { marginLeft: '200px', padding: '2rem', paddingBottom: '5rem' },
  titleRow:           { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', gap: '1rem', flexWrap: 'wrap' },
  title:              { margin: '0 0 0.25rem', color: '#112b49', fontSize: '1.5rem' },
  subtitle:           { margin: 0, color: '#555', fontSize: '0.9rem' },
  errorBanner:        { backgroundColor: '#fee2e2', border: '1px solid #fca5a5', color: '#7f1d1d', borderRadius: '6px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem' },
  uploadProgressBar:  { position: 'relative', height: '28px', backgroundColor: '#e5e7eb', borderRadius: '4px', marginBottom: '1rem', overflow: 'hidden' },
  uploadProgressFill: { position: 'absolute', top: 0, left: 0, height: '100%', backgroundColor: 'cornflowerblue', transition: 'width 0.2s' },
  uploadProgressText: { position: 'absolute', width: '100%', textAlign: 'center', lineHeight: '28px', fontSize: '0.8rem', fontWeight: '600', color: '#112b49' },

  card:               { backgroundColor: 'rgba(255,255,255,0.96)', borderRadius: '10px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', gap: '1rem' },
  cardTitle:          { margin: 0, fontSize: '1.15rem', color: '#112b49' },
  cardText:           { margin: 0, fontSize: '0.9rem', color: '#374151', lineHeight: 1.5, maxWidth: '680px' },
  sysRow:             { display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #e5e7eb', paddingTop: '0.8rem' },
  sysTitle:           { fontSize: '0.95rem', fontWeight: 600, color: '#112b49' },
  printsLine:         { fontSize: '0.78rem', color: '#4b5563' },
  checklistFoot:      { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem', flexWrap: 'wrap' },

  progressCard:       { backgroundColor: 'rgba(255,255,255,0.96)', borderRadius: '10px', padding: '1rem 1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', justifyContent: 'space-between' },
  stat:               { display: 'flex', flexDirection: 'column' },
  statNum:            { fontSize: '1.5rem', fontWeight: 700, color: '#112b49', lineHeight: 1.1 },
  statLabel:          { fontSize: '0.75rem', color: '#4b5563' },

  slotList:           { display: 'flex', flexDirection: 'column', gap: '1rem' },
  slotCard:           { backgroundColor: 'rgba(255,255,255,0.96)', borderRadius: '10px', padding: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', gap: '0.85rem' },
  slotHighlight:      { boxShadow: '0 0 0 3px #6495ED', transition: 'box-shadow 0.3s' },
  slotHeader:         { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
  slotTitle:          { margin: 0, fontSize: '1rem', color: '#112b49', fontWeight: 600 },
  photoNumBadge:      { backgroundColor: '#112b49', color: 'white', fontSize: '0.75rem', fontWeight: '700', padding: '0.2rem 0.55rem', borderRadius: '3px', letterSpacing: '0.05em' },
  extraBadge:         { backgroundColor: '#7f1d1d', color: 'white', fontSize: '0.72rem', fontWeight: '700', padding: '0.15rem 0.5rem', borderRadius: '3px', letterSpacing: '0.05em' },
  pill:               { fontSize: '0.75rem', fontWeight: 600, borderRadius: '999px', padding: '0.25rem 0.65rem' },
  pillConfirmed:      { backgroundColor: '#dcfce7', color: '#14532d' },
  pillAi:             { backgroundColor: '#dbeafe', color: '#1e3a8a' },
  pillEdited:         { backgroundColor: '#ffedd5', color: '#7c2d12' },
  pillNa:             { backgroundColor: '#fef3c7', color: '#78350f' },
  flag:               { fontSize: '0.85rem', color: '#7c2d12', backgroundColor: '#fff7ed', border: '1px solid #fdba74', borderRadius: '6px', padding: '0.5rem 0.75rem' },
  naRow:              { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#374151', cursor: 'pointer', alignSelf: 'flex-start' },
  naPanel:            { width: '320px', maxWidth: '100%', height: '150px', border: '1px solid #9ca3af', backgroundColor: '#eceef1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, letterSpacing: '0.08em', color: '#374151', borderRadius: '4px' },

  strip:              { display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' },
  thumbFig:           { margin: 0, width: '220px', display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  thumbnail:          { width: '100%', height: '165px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #e5e7eb', backgroundColor: '#f3f4f6' },
  missingThumb:       { width: '100%', height: '165px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #c3cad5', borderRadius: '4px', fontSize: '0.8rem', color: '#6b7280' },
  thumbCap:           { fontSize: '0.75rem', color: '#111827' },
  addTile:            { width: '220px', height: '165px', border: '2px dashed #c3cad5', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', boxSizing: 'border-box' },

  linesBox:           { display: 'flex', flexDirection: 'column', gap: '0.7rem', backgroundColor: '#f6f8fb', borderRadius: '8px', padding: '0.85rem 1rem' },
  lineBlock:          { display: 'flex', flexDirection: 'column', gap: '0.3rem', borderTop: '1px solid #e5e9ef', paddingTop: '0.6rem' },
  lineLabel:          { fontSize: '0.83rem', color: '#1f2937', fontWeight: 500 },
  optTag:             { fontWeight: 400, color: '#6b7280', fontSize: '0.75rem' },
  lockedVal:          { fontSize: '0.83rem', color: '#78350f', fontWeight: 600 },
  detailsTitle:       { fontSize: '0.78rem', fontWeight: 700, color: '#112b49', textTransform: 'uppercase', letterSpacing: '0.05em' },
  repairBox:          { display: 'flex', flexDirection: 'column', gap: '0.75rem', border: '1px solid #fecaca', backgroundColor: '#fffafa', borderRadius: '8px', padding: '0.85rem 1rem' },
  repairRow:          { display: 'flex', flexDirection: 'column', gap: '0.5rem', borderTop: '1px dashed #fca5a5', paddingTop: '0.6rem' },
  inlineLabel:        { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#374151' },
  slotFoot:           { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', borderTop: '1px solid #eef0f3', paddingTop: '0.75rem' },

  textInput:          { padding: '0.45rem 0.7rem', border: '1px solid #c3cad5', borderRadius: '6px', fontSize: '0.88rem', fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' },
  chip:               { backgroundColor: '#e0e7ff', color: '#312e81', border: 'none', borderRadius: '999px', padding: '0.25rem 0.7rem', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit' },
  warnText:           { fontSize: '0.78rem', color: '#7f1d1d' },
  btnRow:             { display: 'flex', gap: '0.45rem', flexWrap: 'wrap' },
  choiceOff:          { ...choiceBase, border: '1px solid #c3cad5', backgroundColor: '#ffffff', color: '#374151' },
  choiceOnPass:       { ...choiceBase, border: '2px solid #047857', backgroundColor: '#d1fae5', color: '#064e3b', fontWeight: 600 },
  choiceOnFail:       { ...choiceBase, border: '2px solid #b91c1c', backgroundColor: '#fee2e2', color: '#7f1d1d', fontWeight: 600 },
  choiceOnNa:         { ...choiceBase, border: '2px solid #b45309', backgroundColor: '#fef3c7', color: '#78350f', fontWeight: 600 },
  primaryBtn:         { padding: '0.5rem 1rem', backgroundColor: '#112b49', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit', minHeight: '36px' },
  primaryBtnLg:       { minHeight: '44px', padding: '0 1.4rem', backgroundColor: '#112b49', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  disabledBtnLg:      { minHeight: '44px', padding: '0 1.4rem', backgroundColor: '#e5e9ef', color: '#4b5563', border: '1px solid #c3cad5', borderRadius: '8px', fontSize: '0.95rem', fontWeight: 600, cursor: 'not-allowed', fontFamily: 'inherit' },
  uploadLabel:        { display: 'inline-flex', alignItems: 'center', padding: '0.5rem 1rem', backgroundColor: 'cornflowerblue', color: 'white', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit', minHeight: '36px', boxSizing: 'border-box' },
  secondaryBtn:       { padding: '0.4rem 0.85rem', backgroundColor: '#ffffff', color: '#112b49', border: '1px solid #112b49', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit', minHeight: '34px' },
  secondaryBtnLg:     { minHeight: '44px', padding: '0 1.1rem', backgroundColor: '#ffffff', color: '#112b49', border: '1px solid #112b49', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  removeBtn:          { padding: '0.3rem 0.7rem', backgroundColor: '#fee2e2', color: '#7f1d1d', border: 'none', borderRadius: '4px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', minHeight: '30px' },
  iconBtn:            { padding: '0.3rem 0.6rem', backgroundColor: '#ffffff', color: '#112b49', border: '1px solid #c3cad5', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit', minHeight: '30px' },
  linkBtn:            { background: 'none', border: 'none', padding: 0, color: 'cornflowerblue', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'inherit' },
  confirmBtn:         { minHeight: '44px', padding: '0 1.25rem', backgroundColor: '#112b49', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  disabledBtn:        { minHeight: '44px', padding: '0 1.25rem', backgroundColor: '#e5e9ef', color: '#4b5563', border: '1px solid #c3cad5', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'not-allowed', fontFamily: 'inherit' },
  checkRow:           { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', color: '#374151', cursor: 'pointer' },

  modalOverlay:       { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalBox:           { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '900px', height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  modalHeader:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', gap: '1rem', flexWrap: 'wrap' },
  modalTitle:         { fontWeight: '600', color: '#112b49', fontSize: '1rem' },
  modalClose:         { background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#4b5563', minWidth: '36px', minHeight: '36px' },
  modalEmpty:         { padding: '2rem', textAlign: 'center', color: '#4b5563' },
  pickerGrid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem', padding: '1rem' },
  pickerCard:         { border: '2px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column', backgroundColor: '#000' },
  pickerImgBtn:       { padding: 0, border: 'none', background: 'none', cursor: 'pointer', display: 'block' },
  pickerThumb:        { width: '100%', height: '240px', objectFit: 'contain', display: 'block' },
  pickerCaption:      { padding: '0.45rem 0.6rem', display: 'flex', flexDirection: 'column', gap: '2px', backgroundColor: '#fafafa', fontSize: '0.75rem' },
  confirmBanner:      { backgroundColor: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '6px', padding: '0.75rem 1rem', margin: '0.75rem 1rem', fontSize: '0.875rem', color: '#78350f' },
  confirmYes:         { padding: '0.4rem 0.9rem', backgroundColor: '#b45309', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit' },
  confirmNo:          { padding: '0.4rem 0.9rem', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit' },
};

export default StructuralPhotoManager;
